import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';
import { adminApi, type ProductCategory } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

type Form = {
  id?: number;
  slug: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  sortOrder: number;
  isVisible: boolean;
};

const emptyForm = (): Form => ({
  slug: '',
  name: '',
  description: '',
  icon: '',
  color: '#FF95CE',
  sortOrder: 0,
  isVisible: true,
});

function toPayload(f: Form): Partial<ProductCategory> {
  return {
    slug: f.slug,
    name: f.name,
    description: f.description || null,
    icon: f.icon || null,
    color: f.color || null,
    sortOrder: f.sortOrder,
    isVisible: f.isVisible,
  };
}

export function AdminCategories() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'categories'], queryFn: adminApi.categories.list });
  const { data: allTab } = useQuery({
    queryKey: ['admin', 'categories', 'all-tab'],
    queryFn: adminApi.categories.allTab,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(emptyForm());

  function invalidateCategoryQueries() {
    qc.invalidateQueries({ queryKey: ['admin', 'categories'] });
    qc.invalidateQueries({ queryKey: ['shop', 'categories'] });
  }

  const createMut = useMutation({
    mutationFn: () => adminApi.categories.create(toPayload(form)),
    onSuccess: () => {
      toast.success('Создано');
      invalidateCategoryQueries();
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const updateMut = useMutation({
    mutationFn: () => adminApi.categories.update(form.id!, toPayload(form)),
    onSuccess: () => {
      toast.success('Сохранено');
      invalidateCategoryQueries();
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => adminApi.categories.remove(id),
    onSuccess: () => {
      toast.success('Удалено');
      invalidateCategoryQueries();
    },
    onError: () => toast.error('Не удалось удалить'),
  });

  const allTabMut = useMutation({
    mutationFn: (isVisible: boolean) => adminApi.categories.updateAllTab({ isVisible }),
    onSuccess: () => {
      toast.success('Сохранено');
      qc.invalidateQueries({ queryKey: ['admin', 'categories', 'all-tab'] });
      qc.invalidateQueries({ queryKey: ['shop', 'categories'] });
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  function openCreate() {
    setForm(emptyForm());
    setOpen(true);
  }

  function openEdit(c: ProductCategory) {
    setForm({
      id: c.id,
      slug: c.slug,
      name: c.name,
      description: c.description ?? '',
      icon: c.icon ?? '',
      color: c.color ?? '#FF95CE',
      sortOrder: c.sortOrder,
      isVisible: c.isVisible,
    });
    setOpen(true);
  }

  const isAllTabVisible = allTab?.isVisible ?? true;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-eol-white">Категории товаров</h1>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" /> Создать
        </Button>
      </div>

      <div className="space-y-3">
        <Card>
          <CardContent className="pt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div
                className="h-8 w-8 rounded-md ring-1 ring-white/10"
                style={{ background: allTab?.color ?? '#FF95CE' }}
                aria-hidden
              />
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-eol-white">{allTab?.name ?? 'Всё'}</h3>
                  <Badge variant="secondary">Системная</Badge>
                  {!isAllTabVisible && <Badge variant="danger">Скрыта</Badge>}
                </div>
                <p className="text-xs text-eol-desc mt-1">
                  all - общая вкладка магазина
                </p>
              </div>
            </div>
            <Button
              size="icon"
              variant="secondary"
              onClick={() => allTabMut.mutate(!isAllTabVisible)}
              title={isAllTabVisible ? 'Скрыть' : 'Показать'}
              disabled={allTabMut.isPending}
            >
              {isAllTabVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </CardContent>
        </Card>
        {data?.map((c) => (
          <Card key={c.id}>
            <CardContent className="pt-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div
                  className="h-8 w-8 rounded-md ring-1 ring-white/10"
                  style={{ background: c.color ?? '#FF95CE' }}
                  aria-hidden
                />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-eol-white">{c.name}</h3>
                    {!c.isVisible && <Badge variant="danger">Скрыта</Badge>}
                  </div>
                  <p className="text-xs text-eol-desc mt-1">
                    {c.slug}
                    {c.description ? ` - ${c.description}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="icon" variant="secondary" onClick={() => openEdit(c)} title="Редактировать">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    if (confirm(`Удалить категорию "${c.name}"? Товары останутся без категории.`)) {
                      removeMut.mutate(c.id);
                    }
                  }}
                  title="Удалить"
                  disabled={removeMut.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {data && data.length === 0 && (
          <p className="text-eol-desc text-sm">Категорий пока нет. Создайте первую.</p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Редактировать категорию' : 'Новая категория'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Название" className="col-span-2">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Код (латиница, для URL)">
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="passes"
              />
            </Field>
            <Field label="Цвет">
              <Input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
              />
            </Field>
            <Field label="Иконка (lucide-name)">
              <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="ticket" />
            </Field>
            <Field label="Порядок сортировки">
              <Input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
              />
            </Field>
            <Field label="Описание" className="col-span-2">
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Field label="Статус" className="col-span-2">
              <label className="flex items-center gap-2 h-10 text-eol-white">
                <input
                  type="checkbox"
                  checked={form.isVisible}
                  onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
                />
                Видна на сайте
              </label>
            </Field>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button onClick={() => (form.id ? updateMut.mutate() : createMut.mutate())}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1.5 justify-end ${className ?? ''}`}>
      <span className="text-xs uppercase font-semibold tracking-wider text-eol-desc">{label}</span>
      {children}
    </label>
  );
}
